import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import { Empty, Input, Pagination, Skeleton } from 'antd'
import { get } from '@/utils/request'
import { NEWS_CATEGORIES, newsCategoryLabel, NewsItem } from '@/utils/news'
import { serverGet } from '@/lib/serverApi'
import { absUrl, breadcrumbLd, SeoProps } from '@/utils/seo'

const PAGE_SIZE = 12

// 站内资讯中心。这里是所有资讯的中间节点（hub）：先在这里成文、沉淀阅读量与评论，
// 再择优搬运到公众号等外部渠道，外部渠道回链到这里。
type Props = { category: string; keywords: string; page: number; initial?: { posts: NewsItem[]; total: number } | null }

const NewsIndex: React.FC<Props> = ({ category, keywords, page, initial }) => {
  const router = useRouter()
  // 首屏列表服务端已取好(爬虫可读),客户端只在切栏目/翻页/搜索时再取
  const [items, setItems] = useState<NewsItem[]>(initial?.posts ?? [])
  const [total, setTotal] = useState(initial?.total ?? 0)
  const [loading, setLoading] = useState(!initial)
  const [kw, setKw] = useState(keywords)
  const skipFirst = useRef(!!initial)

  const load = useCallback(() => {
    if (skipFirst.current) {
      skipFirst.current = false
      return
    }
    setLoading(true)
    get('users/posts', { page: page - 1, pageSize: PAGE_SIZE, category, key: keywords }).then((res: any) => {
      if (res?.ok) {
        setItems(res.posts ?? [])
        setTotal(res.total ?? 0)
      }
      setLoading(false)
    })
  }, [category, keywords, page])

  useEffect(load, [load])
  useEffect(() => setKw(keywords), [keywords])

  const navigate = (next: { category?: string; keywords?: string; page?: number }) => {
    const q = { category, keywords, page, ...next }
    router.push(`/news?category=${q.category}&keywords=${encodeURIComponent(q.keywords)}&page=${q.page}`)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-6">
          <h1 className="mb-1 text-3xl font-bold text-gray-900">资讯</h1>
          <p className="text-sm text-gray-500">产品更新、规则解读、赛事动态与使用指南 —— 灵缇赛事系统的一手消息都在这里。</p>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {NEWS_CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => navigate({ category: c.key, page: 1 })}
                className={`cursor-pointer rounded-full border border-solid px-3 py-1 text-sm transition-colors ${
                  category === c.key
                    ? 'border-[#DB3142] bg-[#DB3142] text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <Input.Search
            className="ml-auto w-full sm:w-64"
            placeholder="搜索资讯"
            value={kw}
            onChange={(e) => setKw(e.target.value)}
            onSearch={(v) => navigate({ keywords: v, page: 1 })}
            allowClear
          />
        </div>

        {loading ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-2xl bg-white p-5 ring-1 ring-black/5">
                <Skeleton active paragraph={{ rows: 2 }} />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl bg-white py-16 ring-1 ring-black/5">
            <Empty description="暂无资讯" />
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <Link
                key={item.id}
                href={`/post/${item.short_id || item.id}`}
                className="flex gap-4 rounded-2xl bg-white p-4 no-underline ring-1 ring-black/5 transition-shadow hover:shadow-md sm:p-5"
              >
                {item.cover && (
                  <div className="hidden w-40 shrink-0 overflow-hidden rounded-xl bg-gray-100 sm:block">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={item.cover} alt={item.title} loading="lazy" className="h-24 w-full object-cover" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    {item.pinned && (
                      <span className="rounded bg-[#DB3142]/10 px-1.5 py-0.5 text-xs font-medium text-[#DB3142]">置顶</span>
                    )}
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">{newsCategoryLabel(item.category)}</span>
                  </div>
                  <h2 className="mb-1 truncate text-lg font-semibold text-gray-900">{item.title}</h2>
                  {item.summary && <p className="mb-2 line-clamp-2 text-sm leading-relaxed text-gray-500">{item.summary}</p>}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                    <span>{dayjs(item.published_at || item.created_at).format('YYYY-MM-DD')}</span>
                    <span>阅读 {item.view_count ?? 0}</span>
                    <span>评论 {item.comment_count ?? 0}</span>
                    {(item.tags ?? []).slice(0, 3).map((t) => (
                      <span key={t} className="text-gray-400">#{t}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="mt-6 flex justify-end">
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              showSizeChanger={false}
              onChange={(p) => navigate({ page: p })}
              itemRender={(p, type, el) =>
                type === 'page' ? (
                  <a href={`/news?category=${category}&keywords=${encodeURIComponent(keywords)}&page=${p}`} onClick={(e) => e.preventDefault()}>
                    {p}
                  </a>
                ) : (
                  el
                )
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}

export const getServerSideProps = async (ctx: any) => {
  const category = String(ctx.query.category ?? 'all')
  const keywords = String(ctx.query.keywords ?? '')
  const page = Number(ctx.query.page ?? 1) || 1
  const res = await serverGet<any>('users/posts', { page: page - 1, pageSize: PAGE_SIZE, category, key: keywords })
  const initial = res?.ok ? { posts: res.posts ?? [], total: res.total ?? 0 } : null
  const catLabel = category !== 'all' ? newsCategoryLabel(category) : ''
  const seo: SeoProps = {
    title: `${catLabel ? `${catLabel} · ` : ''}灵缇资讯${page > 1 ? ` · 第${page}页` : ''} · 掼蛋/复式赛/匹克球赛事动态与规则解读`,
    description: '灵缇赛事系统官方资讯:掼蛋、桥牌、复式赛、团团转、瑞士制等赛制规则解读,全国各地赛事战报,复式发牌机与产品更新,办赛经验与使用指南。',
    // 只有无筛选的第一页作为 canonical,筛选/搜索/翻页页不重复收录
    path: category === 'all' && !keywords ? (page > 1 ? `/news?page=${page}` : '/news') : '/news',
    noindex: !!keywords,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '灵缇资讯',
        url: absUrl('/news'),
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: (initial?.posts ?? []).map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: absUrl(`/post/${p.short_id || p.id}`),
            name: p.title,
          })),
        },
      },
      breadcrumbLd([
        ['首页', '/'],
        ['资讯', '/news'],
      ]),
    ],
  }
  return { props: { category, keywords, page, initial, seo } }
}

export default NewsIndex
